import DataTable from "components/DataTable/DataTable";
import { useGetAllVendorInvoiceFilesQuery, useUploadFilesMutation } from "./vendorInvoiceFileSlice";
import { orderBy } from "lodash";
import React, { useState, useMemo } from "react";
import useSortOptions from "hooks/useSortOptions";
import FilterService from "services/filterService";
import VendorInvoicePreview from "../vendor/components/VendorInvoicePreview"
import Authorize from "components/Authorize";
import { invoicePermissionProfile } from "components/Authorize/permissionProfiles/invoicePermissionProfile";
import { userPreferencesComponentIds } from "components/UserPreferences/userPreferencesConsts";

import { filterConfig, getTableColumns } from "./VendorInvoiceFilesConfig";
import LoadingSpinner from "components/LoadingSpinner/LoadingSpinner";
import MultiDropZoneDialog from "components/MultiDropZoneDialog/MultiDropZoneDialog";
import { IconAdd } from "icons";
import { Button, Grid } from "@material-ui/core";
import NotificationProvider from "components/NotificationProvider";


const VendorInvoiceFilesPage = () => {
    const { data: vendorInvoiceFiles, isLoading, error } = useGetAllVendorInvoiceFilesQuery();
    const files = vendorInvoiceFiles || [];
    const [sortOptions, sortData] = useSortOptions();
    const [filters, setFilters] = useState({});
    const [selectedInvoice, setSelectedInvoice] = useState(null);
    const [uploadFiles] = useUploadFilesMutation();

    const updateFilters = (prop, value) => setFilters({ ...filters, [prop]: { value } });
    const onDropFilter = (filterValue, prop) => updateFilters(prop, filterValue);
    const onResetFilters = () => setFilters({});
    const [openDialog, setOpenDialog] = useState(false);

    const tableColumns = useMemo(() => getTableColumns(setSelectedInvoice),
     [setSelectedInvoice]);

    const mapExportData = useMemo(() => (row => {
        const exportColumns = tableColumns.filter(col => col.name !== "Output");
        const exportData = {};
        exportColumns.forEach(col => {
            exportData[col.name] = row[col.key];
        });
        exportData['Status'] = row?.statusLabel;
        exportData['File'] = row?.fileUrl;
        exportData['Invoice'] = row?.vendorInvoiceId;
        return exportData;
    }), [tableColumns]);


    const handleUploadFiles = async (files) => {
        if (!files || files.length === 0) return [];
        
        try {
            const results = await uploadFiles(files);
            if (results?.error) {
                setOpenDialog(false);
                NotificationProvider.error("File upload failed. Please try again.");
                return [];
            }
           return {
            files: files.map(f => {
                const res = Array.isArray(results) ? results.find(r => r.fileName === f.name) : null;
                return {
                    fileName: f.name,
                    success: res ? res.success : true,
                    error: res ? res.error : ""
                };
            })
        };
        } catch (err) {
            setOpenDialog(false);
            NotificationProvider.error("Unexpected error. Please try again.");
            return [];
        }
    };


    const filteredFiles = FilterService.filter(filters, null, files, filterConfig);

    return (
        <Authorize profile={invoicePermissionProfile.VIEW_VENDOR_INVOICE_FILE}>
                {isLoading ? (
                    <LoadingSpinner loading={isLoading} />
                ) : error ? (
                    <div style={{ padding: 24, color: 'red' }}>Error loading vendor invoice files.</div>
                ) : (
                    <>
                        <DataTable
                            columns={tableColumns}
                            allowColumnCustomization
                            tableId={userPreferencesComponentIds.VENDOR_INVOICE_FILE_TABLE}
                            rows={orderBy(
                                filteredFiles,
                                sortOptions.columnToSort,
                                sortOptions.sortDirection
                            )}
                            filters={filters}
                            mapExportData={mapExportData}
                            onDropFilter={onDropFilter}
                            dropDownsConfig={filterConfig}
                            rowIdentifier='id'
                            onSort={sortData}
                            sortDirection={sortOptions.sortDirection}
                            columnToSort={sortOptions.columnToSort}
                            noItemsMessage='There are no vendor invoice files to display'
                            withFilterPanel={true}
                            onResetFilters={onResetFilters}
                            title="Vendor Invoice Files"
                            actionButtons={<Authorize profile={invoicePermissionProfile.EDIT_VENDOR_INVOICE_FILE}>
                            <Grid container alignItems="center">
                                <Button
                                    variant='contained'
                                    color='primary'
                                    onClick={() => setOpenDialog(true)}
                                    startIcon={<IconAdd />}
                                >
                                    Upload Invoice
                                </Button>
                            </Grid>
                        </Authorize>}
                        />
                        <VendorInvoicePreview
                            invoice={selectedInvoice}
                            onClose={() => {
                                setSelectedInvoice(null);
                            }}
                        />
                        <MultiDropZoneDialog
                        open={openDialog}
                        onClose={() => setOpenDialog(false)}
                        labelDialog="Upload Invoice Documents"
                        onSave={handleUploadFiles}
                        accept={{
                            'application/pdf': ['.pdf'],
                            'image/jpeg': ['.jpeg', '.jpg'],
                            'application/vnd.ms-excel': ['.xls'],
                            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
                            'text/csv': ['.csv']
                        }}
                    />
                    </>

                )}
         </Authorize>
    );
};

export default VendorInvoiceFilesPage;